import { WireTypes } from "@flakiness/server/common/wireTypes.js";
import { html, nothing, TemplateResult } from "lit";
import { forgeUtils } from "../forgeUtils.js";

/**
 * A nav/panel pair, the shape `otherCITabs()` hands back. Used for tab sets
 * that contribute more than one tab.
 */
export type CITabPair = { navs: TemplateResult, panels: TemplateResult };

export type CITabsOptions = {
  idPrefix: string,
  project: WireTypes.Project,
  githubActions: TemplateResult,
  gitlabCI: TemplateResult,
  // A single "Other CI" panel, or a pre-built pair for runners that split it
  // per-OS (the `@flakiness/junit-xml` ones).
  otherCI: TemplateResult | CITabPair,
};

/**
 * The "Configure your CI" tab strip.
 *
 * A project linked to a forge only gets that forge's OIDC tab: the other one
 * could never authenticate anyway, since Flakiness.io checks the OIDC token's
 * repository claim against the link. Projects with nothing behind them (local
 * checkouts, JSON repos) get both, because either one may be set up later.
 */
export function ciTabs(opts: CITabsOptions): TemplateResult {
  const showGithub = !forgeUtils.isGitlab(opts.project);
  const showGitlab = !forgeUtils.isGithub(opts.project);
  const githubId = `${opts.idPrefix}-github-actions`;
  const gitlabId = `${opts.idPrefix}-gitlab-ci`;
  const other = isTabPair(opts.otherCI) ? opts.otherCI : singleOtherCITab(opts.idPrefix, opts.otherCI);
  return html`
    <sl-tab-group>
      ${showGithub ? html`<sl-tab slot="nav" panel="${githubId}">GitHub Actions</sl-tab>` : nothing}
      ${showGitlab ? html`<sl-tab slot="nav" panel="${gitlabId}">GitLab CI/CD</sl-tab>` : nothing}
      ${other.navs}
      ${showGithub ? html`<sl-tab-panel name="${githubId}">${opts.githubActions}</sl-tab-panel>` : nothing}
      ${showGitlab ? html`<sl-tab-panel name="${gitlabId}">${opts.gitlabCI}</sl-tab-panel>` : nothing}
      ${other.panels}
    </sl-tab-group>
  `;
}

function isTabPair(value: TemplateResult | CITabPair): value is CITabPair {
  return 'navs' in value;
}

function singleOtherCITab(idPrefix: string, panel: TemplateResult): CITabPair {
  const id = `${idPrefix}-other-ci`;
  return {
    navs: html`<sl-tab slot="nav" panel="${id}">Other CI</sl-tab>`,
    panels: html`<sl-tab-panel name="${id}">${panel}</sl-tab-panel>`,
  };
}
